import { useState, useEffect } from "react";
import { supabase } from '../supabase';
import { logActivity } from './ActivityLog';

const C = {
  bg:"#0e0e0e", sidebar:"#0a0a0a", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};
const card  = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp   = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn   = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl   = t => <div style={{fontSize:11,color:C.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;
const secTitle = t => <div style={{fontSize:11,color:C.muted,marginBottom:12,letterSpacing:0.5,textTransform:"uppercase"}}>{t}</div>;

const roleCfg = {
  admin:    { label:"Admin",    color:"#34d399" },
  danser:   { label:"Danser",   color:"#E8197D" },
  vokalist: { label:"Vokalist", color:"#a78bfa" },
  tm:       { label:"TM",       color:"#F5A623" },
  akrobat:  { label:"Akrobat",  color:"#FF5F1F" },
  musiker:  { label:"Musiker",  color:"#60a5fa" },
  tekniker: { label:"Tekniker", color:"#94a3b8" },
};

function Avatar({ initials, size=36, role }) {
  const color = roleCfg[role]?.color || C.orange;
  return (
    <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,border:`1px solid ${color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size>30?13:10,color,fontWeight:500,flexShrink:0}}>
      {initials}
    </div>
  );
}

function Toast({ msg, ok=true }) {
  if(!msg) return null;
  return <div style={{position:"fixed",bottom:72,left:"50%",transform:"translateX(-50%)",background:C.surface,border:`0.5px solid ${ok?C.green:C.red}44`,color:ok?C.green:C.red,padding:"10px 20px",borderRadius:20,fontSize:13,zIndex:400,whiteSpace:"nowrap"}}>{msg}</div>;
}

export default function BroadcastPage({ user, users, projects, onNotify, isMobile }) {
  const [tab, setTab] = useState("new");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [sendPush, setSendPush] = useState(false);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState({ msg:"", ok:true });
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Mottaker-valg
  const [recipientMode, setRecipientMode] = useState("all"); // "all" | "project" | "custom"
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const showToast = (msg, ok=true) => { setToast({msg,ok}); setTimeout(()=>setToast({msg:"",ok:true}),2500); };

  const activeUsers = users.filter(u => u.active);
  const activeProjects = projects.filter(p => !p.archived);

  // Beregn faktiske mottakere basert på valg
  const getRecipients = () => {
    if(recipientMode === "all") return activeUsers;
    if(recipientMode === "project") {
      const proj = projects.find(p => p.id === selectedProject);
      if(!proj) return [];
      return activeUsers.filter(u => proj.team.includes(u.id));
    }
    if(recipientMode === "custom") return activeUsers.filter(u => selectedUsers.includes(u.id));
    return [];
  };

  const toggleUser = (uid) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(x=>x!==uid) : [...prev, uid]);
  };

  const recipients = getRecipients();
  const linkedProject = recipientMode === "project" ? projects.find(p => p.id === selectedProject) : null;

  // Last inn logg
  useEffect(() => {
    if(tab === "log") loadLog();
  }, [tab]);

  const loadLog = async () => {
    setLogLoading(true);
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('type', 'broadcast')
      .order('created_at', { ascending: false });
    if(data) setLog(data);
    setLogLoading(false);
  };

  const send = async () => {
    if(!title.trim() || !message.trim()) { showToast("Tittel og melding er påkrevd.", false); return; }
    if(recipients.length === 0) { showToast("Ingen mottakere valgt.", false); return; }
    setSending(true);

    // Lagre som varsel i systemet
    const notif = {
      type: "broadcast",
      project_id: linkedProject?.id || null,
      project_name: linkedProject?.name || null,
      recipient_ids: recipients.map(u => u.id),
      read: {},
      broadcast_title: title,
      broadcast_message: message,
      broadcast_sender: user.name,
      broadcast_sender_id: user.id,
    };
    const { error } = await supabase.from('notifications').insert(notif);
    if(error) { showToast("Kunne ikke sende melding.", false); setSending(false); return; }

    // Send e-post hvis valgt
    if(sendEmail) {
      await Promise.all(recipients.map(r =>
        supabase.functions.invoke("send-notification", {
          body: {
            type: "broadcast",
            recipient_email: r.email,
            recipient_name: r.name,
            sender_name: user.name,
            broadcast_title: title,
            broadcast_message: message,
            project_name: linkedProject?.name || null,
            project_date: linkedProject?.date || null,
          }
        })
      ));
    }


    logActivity(user, 'broadcast_sent', { broadcast_title: title, recipient_count: recipients.length }, 'broadcast');
    if(sendPush) {
      await supabase.functions.invoke("send-push", {
        body: {
          user_ids: recipients.map(u => u.id),
          title: title.trim(),
          body: message.trim().slice(0, 100),
        }
      });
    }
    showToast(`Melding sendt til ${recipients.length} mottaker${recipients.length>1?"e":""}! ${sendEmail?"📧":""} ${sendPush?"🔔":""}`);
    setTitle("");
    setMessage("");
    setSelectedUsers([]);
    setSelectedProject("");
    setRecipientMode("all");
    setSendEmail(false);
    setSending(false);
  };

  const fmtDate = d => new Date(d+"Z").toLocaleDateString("nb-NO",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});

  return (
    <div style={{paddingBottom:isMobile?80:0}}>
      <Toast msg={toast.msg} ok={toast.ok}/>
      {gradBar}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <div style={{fontSize:20,fontWeight:500}}>Meldinger</div>
        <div style={{display:"flex",gap:8}}>
          {["new","log"].map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{...btn(tab===t?"primary":"ghost"),padding:"6px 16px",fontSize:12}}>
              {t==="new"?"✉️ Ny melding":"📋 Logg"}
            </button>
          ))}
        </div>
      </div>

      {tab === "new" && (
        <div style={{maxWidth:600}}>

          {/* Tittel */}
          <div style={{...card(),marginBottom:16}}>
            {secTitle("Melding")}
            <div style={{marginBottom:14}}>
              {lbl("Tittel")}
              <input style={inp()} value={title} onChange={e=>setTitle(e.target.value)} placeholder="F.eks. Viktig info om helgens show"/>
            </div>
            <div>
              {lbl("Melding")}
              <textarea style={inp({minHeight:120,resize:"vertical"})} value={message} onChange={e=>setMessage(e.target.value)} placeholder="Skriv meldingen din her..."/>
            </div>
          </div>

          {/* Mottakere */}
          <div style={{...card(),marginBottom:16}}>
            {secTitle("Mottakere")}
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
              {[
                {k:"all", l:"Alle"},
                {k:"project", l:"Prosjekt"},
                {k:"custom", l:"Velg selv"},
              ].map(({k,l})=>(
                <button key={k} onClick={()=>setRecipientMode(k)} style={{padding:"6px 14px",borderRadius:20,fontSize:12,cursor:"pointer",border:`0.5px solid ${recipientMode===k?C.orange:C.border}`,background:recipientMode===k?"#FF5F1F22":"transparent",color:recipientMode===k?C.orange:C.muted}}>
                  {l}
                </button>
              ))}
            </div>

            {/* Prosjektvalg */}
            {recipientMode === "project" && (
              <div style={{marginBottom:16}}>
                {lbl("Velg prosjekt")}
                <select style={inp()} value={selectedProject} onChange={e=>setSelectedProject(e.target.value)}>
                  <option value="">– Velg prosjekt –</option>
                  {activeProjects.map(p=>(
                    <option key={p.id} value={p.id}>{p.name} ({new Date(p.date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short",year:"numeric"})})</option>
                  ))}
                </select>
              </div>
            )}

            {/* Enkeltpersoner */}
            {recipientMode === "custom" && (
              <div style={{marginBottom:16}}>
                {lbl("Velg mottakere")}
                <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
                  {activeUsers.map(u=>(
                    <div key={u.id} onClick={()=>toggleUser(u.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:8,background:selectedUsers.includes(u.id)?`${C.orange}11`:C.surface2,border:`0.5px solid ${selectedUsers.includes(u.id)?C.orange:C.border}`,cursor:"pointer"}}>
                      <div style={{width:18,height:18,borderRadius:4,border:`1.5px solid ${selectedUsers.includes(u.id)?C.orange:C.border}`,background:selectedUsers.includes(u.id)?C.orange:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {selectedUsers.includes(u.id)&&<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M2 6l3 3 5-5"/></svg>}
                      </div>
                      <Avatar initials={u.initials} size={30} role={u.role}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:500}}>{u.name}</div>
                        <div style={{fontSize:11,color:C.muted}}>{roleCfg[u.role]?.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Oppsummering */}
            {recipients.length > 0 && (
              <div style={{padding:"10px 12px",borderRadius:8,background:C.surface2,fontSize:13,color:C.muted}}>
                {recipients.length === activeUsers.length
                  ? `Sendes til alle ${recipients.length} aktive brukere`
                  : `Sendes til ${recipients.length} mottaker${recipients.length>1?"e":""}: ${recipients.map(u=>u.name.split(" ")[0]).join(", ")}`
                }
                {linkedProject && <span style={{color:C.orange}}> · knyttet til «{linkedProject.name}»</span>}
              </div>
            )}
          </div>

          {/* E-post og push toggle */}
          <div style={{...card(),marginBottom:24,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>Send også på e-post</div>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>Mottakerne får meldingen på e-post i tillegg til varselet i appen</div>
              </div>
              <div onClick={()=>setSendEmail(v=>!v)} style={{width:40,height:22,borderRadius:11,background:sendEmail?C.orange:"#333",border:`0.5px solid ${sendEmail?C.orange:C.border}`,position:"relative",cursor:"pointer",flexShrink:0,transition:"background 0.2s"}}>
                <div style={{position:"absolute",top:3,left:sendEmail?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>Send som push-varsel</div>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>Mottakerne får en push-notifikasjon på telefonen</div>
              </div>
              <div onClick={()=>setSendPush(v=>!v)} style={{width:40,height:22,borderRadius:11,background:sendPush?C.orange:"#333",border:`0.5px solid ${sendPush?C.orange:C.border}`,position:"relative",cursor:"pointer",flexShrink:0,transition:"background 0.2s"}}>
                <div style={{position:"absolute",top:3,left:sendPush?20:3,width:16,height:16,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
              </div>
            </div>
          </div>

          {/* Send-knapp */}
          <button
            style={{...btn("primary"),width:"100%",padding:14,fontSize:14}}
            onClick={send}
            disabled={sending||!title.trim()||!message.trim()||recipients.length===0}
          >
            {sending ? "Sender..." : `Send melding${sendEmail?" + e-post":""} →`}
          </button>
        </div>
      )}

      {/* Logg */}
      {tab === "log" && (
        <div>
          {logLoading && <div style={{color:C.muted,textAlign:"center",marginTop:40}}>Laster...</div>}
          {!logLoading && log.length === 0 && (
            <div style={{...card({textAlign:"center",padding:40})}}>
              <div style={{fontSize:14,color:C.muted}}>Ingen meldinger sendt ennå.</div>
            </div>
          )}
          {log.map(n=>(
            <div key={n.id} style={{...card({marginBottom:12})}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontSize:15,fontWeight:500,marginBottom:2}}>{n.broadcast_title}</div>
                  <div style={{fontSize:11,color:C.muted}}>Fra {n.broadcast_sender} · {fmtDate(n.created_at)}</div>
                </div>
                <div style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:`${C.orange}22`,color:C.orange,border:`0.5px solid ${C.orange}44`,flexShrink:0,marginLeft:12}}>
                  {(n.recipient_ids||[]).length} mottakere
                </div>
              </div>
              <div style={{fontSize:13,color:"#aaa",lineHeight:1.6,padding:"10px 12px",background:C.surface2,borderRadius:8,marginBottom:n.project_name?8:0}}>
                {n.broadcast_message}
              </div>
              {n.project_name && (
                <div style={{fontSize:12,color:C.orange,marginTop:8}}>🎭 Knyttet til «{n.project_name}»</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}